import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, CircleDashed, Trophy } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Avatar } from "@/components/avatar";
import { getPipeline } from "@/lib/opportunities.functions";
import { signalKindLabel } from "@/lib/signal-kind";

export const Route = createFileRoute("/_authenticated/pipeline")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: Pipeline,
  head: () => ({
    meta: [
      { title: "Pipeline · Pyng" },
      {
        name: "description",
        content:
          "Every opportunity you acted on, and what came of it — Pyng's running record of relationship ROI.",
      },
      { property: "og:title", content: "Pipeline · Pyng" },
      {
        property: "og:description",
        content: "Track the opportunities you acted on and the outcomes they produced.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function Pipeline() {
  const fetchPipeline = useServerFn(getPipeline);
  const { data } = useQuery({ queryKey: ["pipeline"], queryFn: () => fetchPipeline() });

  const rows = data?.pipeline ?? [];
  const counts = data?.counts ?? { acted: 0, won: 0, closed: 0 };

  return (
    <div className="space-y-6 pb-4">
      <PageHeader eyebrow="Your record" title="Pipeline" />

      <div className="grid grid-cols-3 gap-2">
        <Stat icon={CircleDashed} label="In play" value={counts.acted} />
        <Stat icon={Trophy} label="Won" value={counts.won} />
        <Stat icon={CheckCircle2} label="Closed" value={counts.closed} />
      </div>

      {rows.length === 0 ? (
        <article className="glass rounded-3xl p-8 text-center">
          <p className="text-sm text-foreground/90 font-medium">Nothing here yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Act on an opportunity from Today and it lands here so you can see what it turned into.
          </p>
          <Link
            to="/today"
            className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground"
          >
            Go to Today
          </Link>
        </article>
      ) : (
        <section className="space-y-2">
          {rows.map((o: any) => (
            <article key={o.id} className="glass rounded-2xl p-4 flex items-start gap-3">
              {o.contacts && (
                <Avatar
                  name={o.contacts.name}
                  initials={o.contacts.initials}
                  photoUrl={o.contacts.photo_url}
                  size={36}
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug">{o.headline}</p>
                <p className="mt-0.5 text-[13px] text-muted-foreground">
                  {signalKindLabel(o.event_kind)}
                  {o.contacts && ` · ${o.contacts.name}`}
                  {o.acted_at && ` · ${new Date(o.acted_at).toLocaleDateString()}`}
                </p>
              </div>
              <span className="shrink-0 rounded-full glass-soft px-2.5 py-1 text-[12px] uppercase tracking-wide text-muted-foreground">
                {o.outcome ?? o.status}
              </span>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Trophy;
  label: string;
  value: number;
}) {
  return (
    <div className="glass rounded-2xl p-3 text-center">
      <Icon className="mx-auto h-4 w-4 text-primary" />
      <p className="mt-1.5 text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-[12px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}